import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye, FiGrid, FiLayers, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { stateManagementAPI } from '../../services/stateManagementAPI';
import './StateManagement.css';

const Commissionaries = () => {
  const navigate = useNavigate();
  const { stateId } = useParams();
  const location = useLocation();
  const stateName = location.state?.stateName || 'Unknown State';

  const [commissionaries, setCommissionaries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedCommissionary, setSelectedCommissionary] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalMatching: 0,
    totalCommissionaries: 0,
    totalDistricts: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    headQuarter: ''
  });

  const [errors, setErrors] = useState({
  name: '',
  code: '',
  headQuarter: ''
});
const validateForm = () => {
  const tempErrors = {};
  let isValid = true;

  // Name Validation
  if (!formData.name.trim()) {
    tempErrors.name = 'Commissionary name is required';
    isValid = false;
  } else if (formData.name.trim().length < 2) {
    tempErrors.name = 'Commissionary name must be at least 2 characters';
    isValid = false;
  } else if (formData.name.trim().length > 50) {
    tempErrors.name = 'Commissionary name cannot exceed 50 characters';
    isValid = false;
  }

  // Code Validation
  if (!formData.code.trim()) {
    tempErrors.code = 'Code is required';
    isValid = false;
  } else if (formData.code.trim().length < 2) {
    tempErrors.code = 'Code must be at least 2 characters';
    isValid = false;
  } else if (formData.code.trim().length > 50) {
    tempErrors.code = 'Code cannot exceed 50 characters';
    isValid = false;
  }

  // HeadQuarter Validation
  if (!formData.headQuarter.trim()) {
    tempErrors.headQuarter = 'Headquarter is required';
    isValid = false;
  } else if (formData.headQuarter.trim().length < 2) {
    tempErrors.headQuarter = 'Headquarter must be at least 2 characters';
    isValid = false;
  } else if (formData.headQuarter.trim().length > 50) {
    tempErrors.headQuarter = 'Headquarter cannot exceed 50 characters';
    isValid = false;
  }

  setErrors(tempErrors);
  return isValid;
};
  // Fetch commissionaries from API
  const fetchCommissionaries = async () => {
    try {
      setLoading(true);
      const response = await stateManagementAPI.getCommissionaries(stateId, currentPage, itemsPerPage, searchTerm);

      if (response.success) {
        setCommissionaries(response.data || []);
        setPagination(response.pagination || {
          totalPages: 1,
          totalMatching: 0,
          totalCommissionaries: 0,
          totalDistricts: 0
        });
      }
    } catch (error) {
      console.error('Error fetching commissionaries:', error);
      toast.error('Failed to fetch commissionaries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    fetchCommissionaries();

  }, [stateId, currentPage, searchTerm]);

  const handleCommissionaryClick = (commissionary) => {
  
    navigate(`/districts/${commissionary._id}`, {
      state: {
        commissionaryName: commissionary.name,
        stateName: commissionary.state.name
      }
    });
  };

 const handleAdd = () => {
  setModalMode('add');
  setFormData({
    name: '',
    code: '',
    headQuarter: ''
  });

  setErrors({
    name: '',
    code: '',
    headQuarter: ''
  });

  setShowModal(true);
};

  const handleView = (e, commissionary) => {
    e.stopPropagation();
    navigate(`/districts/${commissionary._id}`, {
      state: {
        commissionaryName: commissionary.name,
        stateName: commissionary?.state?.name
      }
    });
  };

 const handleEdit = (e, commissionary) => {
  e.stopPropagation();

  setModalMode('edit');
  setSelectedCommissionary(commissionary);

  setFormData({
    name: commissionary.name,
    code: commissionary.code,
    headQuarter: commissionary.headQuarter
  });

  setErrors({
    name: '',
    code: '',
    headQuarter: ''
  });

  setShowModal(true);
};

  const handleDelete = (e, commissionary) => {
    e.stopPropagation();
    setItemToDelete(commissionary);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await stateManagementAPI.deleteCommissionary(itemToDelete._id);
      toast.success('Commissionary deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchCommissionaries(); // Refresh the list
    } catch (error) {
      console.error('Error deleting commissionary:', error);
      toast.error('Failed to delete commissionary');
    } finally {
      setLoading(false);
    }
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) return;

  try {
    setLoading(true);

    if (modalMode === 'add') {
      const payload = { ...formData, state: stateId };
      await stateManagementAPI.createCommissionary(payload);
      toast.success('Commissionary added successfully');
    } else {
      await stateManagementAPI.updateCommissionary(
        selectedCommissionary._id,
        formData
      );
      toast.success('Commissionary updated successfully');
    }

    setShowModal(false);
    setErrors({});
    fetchCommissionaries();

  } catch (error) {
    console.error('Error saving commissionary:', error);
    toast.error(
      `Failed to ${modalMode === 'add' ? 'add' : 'update'} commissionary`
    );
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="state-management">
      <div className="page-header">
        <div className="header-content">
          {stateId && <div className="breadcrumb">
            <button
              className="back-btn"
              onClick={() => navigate('/states')}
            >
              <FiArrowLeft />
            </button>
            <span className="breadcrumb-text">States / {stateName} / Commissionaries</span>
          </div>}
          {stateId ? <h1 className="page-title">
            <FiGrid className="title-icon" />
            Commissionaries in {stateName}
          </h1> :
            <h1 className="page-title">
              <FiGrid className="title-icon" />
              All Commissionaries
            </h1>}
          {stateId && <p className="page-subtitle">Manage commissionaries and their districts</p>}
        </div>
        {stateId && <button className="add-btn" onClick={handleAdd}>
          <FiPlus /> Add Commissionery
        </button>}
      </div>

      {/* Dashboard Cards */}
      <div className="dashboard-cards">
        <div className="dashboard-card">
          <div className="card-content">
            <div className="card-info">
              <h3>Total Commissionaries</h3>
              <div className="card-value">{pagination.totalCommissionaries}</div>
            </div>
            <div className="card-icon commissionaries">
              <FiGrid />
            </div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="card-content">
            <div className="card-info">
              <h3>Total Districts</h3>
              <div className="card-value">{pagination.totalDistricts}</div>
            </div>
            <div className="card-icon districts">
              <FiLayers />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="controls-section">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search commissionaries..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when searching
            }}
            className="search-input"
          />
        </div>
      </div>

      {/* Commissionaries Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <p>Loading...</p>
          </div>
        ) : (
          <table className="states-table">
            <thead>
              <tr>
                <th>Commissionary Name</th>
                <th>Code</th>
                <th>Headquarters</th>
                <th>Districts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {commissionaries.length > 0 ? commissionaries.map((commissionary) => (
                <tr key={commissionary._id} className="table-row">
                  <td
                    className="state-name clickable"
                    onClick={() => handleCommissionaryClick(commissionary)}
                  >
                    {commissionary.name[0].toUpperCase()+commissionary.name.slice(1)}
                  </td>
                  <td onClick={() => handleCommissionaryClick(commissionary)}>{commissionary.code}</td>
                  <td onClick={() => handleCommissionaryClick(commissionary)}>{commissionary.headQuarter}</td>
                  <td onClick={() => handleCommissionaryClick(commissionary)}>{commissionary.districtCount || 0}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={(e) => handleView(e, commissionary)}
                        title="View"
                      >
                        <FiEye />
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={(e) => handleEdit(e, commissionary)}
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={(e) => handleDelete(e, commissionary)}
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    No commissionaries found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {pagination.totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
          disabled={currentPage === pagination.totalPages || loading}
        >
          Next
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'add' ? 'Add Commissionary' : 'Edit Commissionary'}
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
             <div className="form-group">
  <label>Commissionary Name</label>

  <input
    type="text"
    value={formData.name}
    onChange={(e) => {
      setFormData({
        ...formData,
        name: e.target.value.slice(0, 50)
      });

      setErrors({
        ...errors,
        name: ''
      });
    }}
  />

  {errors.name && (
    <span className="error-text">{errors.name}</span>
  )}
</div>
             <div className="form-group">
  <label>Code</label>

  <input
    type="text"
    value={formData.code}
    onChange={(e) => {
      setFormData({
        ...formData,
        code: e.target.value.slice(0, 50)
      });

      setErrors({
        ...errors,
        code: ''
      });
    }}
  />

  {errors.code && (
    <span className="error-text">{errors.code}</span>
  )}
</div>
            <div className="form-group">
  <label>Headquarters</label>

  <input
    type="text"
    value={formData.headQuarter}
    onChange={(e) => {
      setFormData({
        ...formData,
        headQuarter: e.target.value.slice(0, 50)
      });

      setErrors({
        ...errors,
        headQuarter: ''
      });
    }}
  />

  {errors.headQuarter && (
    <span className="error-text">{errors.headQuarter}</span>
  )}
</div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={loading}>
                  {loading ? 'Saving...' : (modalMode === 'add' ? 'Add Commissionary' : 'Update Commissionary')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowDeleteModal(false)} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="danger" onClick={confirmDelete} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Commissionaries;